import { cn } from '@/ui/lib/utils'

export type QuantRunStatus = 'running' | 'completed' | 'failed' | 'canceled' | 'orphaned'

export interface QuantRunBadgeProps {
  /** Any wire string; an unknown value renders neutrally with its own text. */
  status: string
  /** A cancel was accepted but the run still reads `running` (spec 038 19.7). */
  canceling?: boolean
  className?: string
}

const STATUS_CLASS: Record<QuantRunStatus, string> = {
  running: 'border-accent-line bg-accent-line text-ink',
  completed: 'border-paper-35 bg-transparent text-paper-90',
  failed: 'border-alert bg-alert text-paper',
  canceled: 'border-[#CB8A00] bg-transparent text-[#CB8A00]',
  orphaned: 'border-dashed border-paper-35 bg-transparent text-paper-40',
}

const CANCELING_CLASS = 'border-[#CB8A00] bg-transparent text-[#CB8A00]'
const UNKNOWN_CLASS = 'border-paper-35 bg-transparent text-paper-50'

/**
 * A run's status badge (spec 036, handoff section 3.4): 9px bold caps, 2x6
 * padding, one visual treatment per status. No `queued` status exists.
 */
export function QuantRunBadge({ status, canceling = false, className }: QuantRunBadgeProps) {
  const label = canceling ? 'canceling' : status
  return (
    <span
      data-run-status={status}
      data-canceling={canceling ? 'true' : undefined}
      title={label}
      className={cn(
        'box-border inline-block w-[76px] min-w-[76px] max-w-[76px] shrink-0 truncate border px-1.5 py-0.5 text-center align-middle font-mono text-[9px] leading-[1.3] font-bold tracking-[0.16em] uppercase',
        canceling
          ? CANCELING_CLASS
          : Object.hasOwn(STATUS_CLASS, status)
            ? STATUS_CLASS[status as QuantRunStatus]
            : UNKNOWN_CLASS,
        className,
      )}
    >
      {label}
    </span>
  )
}
